/**
 * The customer list.
 *
 * The one filter that earns its place on the toolbar rather than in a menu is
 * **over the credit limit**. It is why the recovery team opens this screen at
 * all, the backend has a partial index covering exactly those rows, and it is
 * a handful out of ten thousand — a filter that has to be hunted for is a
 * filter nobody uses.
 *
 * The credit column is a meter, not a number, for the same reason the article
 * list's completion is: the reader is comparing rows to decide who to chase,
 * and a column of percentages makes them do the comparison themselves.
 *
 * Who follows each customer is set here: rows are ticked and given to a commercial in one go,
 * or the whole assignment goes out and comes back as a file. A commercial signing in sees only
 * their own customers in this same list — the server decides, the screen has nothing to hide.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearch } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { download, useAction, useDebounced, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { money } from "@hardauto/core-i18n";
import {
  Alert,
  Button,
  Chips,
  DataTable,
  Dialog,
  Input,
  Meter,
  Page,
  Select,
  StatusDot,
  Toolbar,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { ClientsApi, type AssignmentImportReport, type ClientRow, type PortalStatus } from "../api";
import { creditTone } from "../credit";
import { NAMESPACE } from "../i18n";
import { CLIENTS_PATHS } from "../paths";
import "./clients.css";

const PAGE = 50;
/** The commercial filter's value for "nobody follows them". */
const NO_REP = "__none__";

const STATUS_TONE: Record<PortalStatus, "success" | "neutral" | "danger"> = {
  active: "success",
  pending: "neutral",
  suspended: "danger",
};

export function ClientsScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ClientsApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("clients.client.write");
  // Reading the commercials is what the filter and the assignment pick from; a commercial,
  // limited to their own customers, has neither.
  const mayReadReps = useCan("clients.rep.read");
  const mayAssign = mayWrite && mayReadReps;
  const mayUseFile = useCan("clients.client.all") && mayWrite;
  const linked: { rep?: string } = useSearch({ strict: false });

  const [term, setTerm] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [overLimit, setOverLimit] = useState(false);
  const [repFilter, setRepFilter] = useState(linked.rep ?? "");
  const [cursor, setCursor] = useState<string | null>(null);

  const search = useDebounced(term, 250);

  const fetcher = useCallback(
    () =>
      api.listClients({
        ...(search ? { search } : {}),
        ...(status ? { portal_status: status as PortalStatus } : {}),
        ...(overLimit ? { over_limit_only: true } : {}),
        ...(repFilter === NO_REP ? { without_rep: true } : {}),
        ...(repFilter && repFilter !== NO_REP ? { sales_rep_code: repFilter } : {}),
        ...(cursor ? { after_id: cursor } : {}),
        limit: PAGE,
      }),
    [api, search, status, overLimit, repFilter, cursor],
  );
  const clients = useResource(fetcher);

  const repsFetcher = useCallback(
    () => (mayReadReps ? api.listSalesReps(false) : Promise.resolve({ items: [] })),
    [api, mayReadReps],
  );
  const reps = useResource(repsFetcher);
  const activeReps = reps.data?.items ?? [];

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [report, setReport] = useState<AssignmentImportReport | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const toggle = (id: string) =>
    setSelected((now) => {
      const next = new Set(now);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const narrow = useCallback((change: () => void) => {
    setCursor(null);
    change();
  }, []);

  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [company, setCompany] = useState("");

  const columns: Column<ClientRow>[] = [
    ...(mayAssign
      ? [
          {
            key: "pick",
            header: t("clients.assign.pick"),
            render: (row: ClientRow) => (
              <input
                type="checkbox"
                aria-label={t("clients.assign.pickOne", { code: row.client.code })}
                checked={selected.has(row.client.id)}
                onChange={() => toggle(row.client.id)}
              />
            ),
          },
        ]
      : []),
    {
      key: "code",
      header: t("clients.column.code"),
      render: (row) => (
        <Link to={CLIENTS_PATHS.client.replace("$id", row.client.id)}>
          <span className="hardauto-ref">{row.client.code}</span>
        </Link>
      ),
    },
    {
      key: "company",
      header: t("clients.column.company"),
      render: (row) => row.client.company_name,
    },
    { key: "city", header: t("clients.column.city"), render: (row) => row.client.city || "—" },
    {
      key: "site",
      header: t("clients.column.site"),
      render: (row) => row.client.default_site_code || "—",
    },
    {
      key: "rep",
      header: t("clients.column.rep"),
      render: (row) => row.sales_rep_name || row.client.sales_rep_code || "—",
    },
    {
      key: "credit",
      header: t("clients.column.credit"),
      render: (row) => (
        <Meter
          value={row.client.credit_usage_pct}
          text={money(row.client.credit_limit, i18n.language)}
          label={t("client.credit.usage")}
          tone={creditTone(row.client.credit_usage_pct, row.client.is_over_credit_limit)}
        />
      ),
    },
    {
      key: "access",
      header: t("clients.column.access"),
      render: (row) => <span className="hardauto-num">{row.active_user_count}</span>,
    },
    {
      key: "status",
      header: t("clients.column.status"),
      render: (row) => (
        <StatusDot
          label={t(`client.status.${row.client.portal_status}`)}
          tone={STATUS_TONE[row.client.portal_status]}
        />
      ),
    },
  ];

  const rows = clients.data?.items ?? [];
  const isNarrowed = Boolean(search || status || overLimit || repFilter);
  const allOnPage = rows.length > 0 && rows.every((row) => selected.has(row.client.id));

  function assign() {
    void action.run("assign", async () => {
      try {
        const result = await api.assignClients([...selected], assignTo || null);
        toast.show(t("clients.assign.done", { count: result.assigned }), { tone: "success" });
        setSelected(new Set());
        setAssigning(false);
        clients.reload();
        reps.reload();
      } catch {
        toast.show(t("error.save"), { tone: "danger" });
      }
    });
  }

  function exportFile() {
    void action.run("export", async () => {
      try {
        download(await api.exportAssignments(), "affectations-commerciaux.csv");
      } catch {
        toast.show(t("clients.assign.exportFailed"), { tone: "danger" });
      }
    });
  }

  function importFile(file: File) {
    void action.run("import", async () => {
      try {
        setReport(await api.importAssignments(file));
        clients.reload();
      } catch (error) {
        toast.show(
          error instanceof ApiError && error.status === 422
            ? t("clients.assign.unreadable")
            : t("error.save"),
          { tone: "danger" },
        );
      }
    });
  }

  return (
    <Page
      title={t("clients.title")}
      eyebrow={t("clients.count", { count: clients.data?.total ?? 0 })}
      actions={
        mayWrite ? (
          <>
            {mayUseFile ? (
              <>
                <Button
                  onClick={exportFile}
                  busy={action.isBusy("export")}
                  busyLabel={t("clients.assign.export")}
                >
                  {t("clients.assign.export")}
                </Button>
                <Button
                  onClick={() => fileInput.current?.click()}
                  busy={action.isBusy("import")}
                  busyLabel={t("clients.assign.import")}
                >
                  {t("clients.assign.import")}
                </Button>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".xlsx,.csv"
                  hidden
                  aria-label={t("clients.assign.choose")}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) importFile(file);
                  }}
                />
              </>
            ) : null}
            {!creating ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t("clients.create")}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <Toolbar>
        <div className="hardauto-search">
          <Input
            type="search"
            aria-label={t("clients.searchLabel")}
            placeholder={t("clients.searchPlaceholder")}
            value={term}
            onChange={(event) => narrow(() => setTerm(event.target.value))}
          />
        </div>
        <Chips
          label={t("clients.column.status")}
          allLabel={t("client.terms.all")}
          active={status}
          onSelect={(next) => narrow(() => setStatus(next))}
          chips={[
            { id: "active", label: t("client.status.active") },
            { id: "pending", label: t("client.status.pending") },
            { id: "suspended", label: t("client.status.suspended") },
          ]}
        />
        {/* Its own control rather than a chip: it is not one value of the
            status filter, it combines with it. */}
        <Chips
          label={t("clients.overLimit")}
          active={overLimit ? "over" : null}
          onSelect={(next) => narrow(() => setOverLimit(next === "over"))}
          chips={[{ id: "over", label: t("clients.overLimit") }]}
        />
        {mayReadReps ? (
          <Select
            aria-label={t("clients.repFilter.label")}
            value={repFilter}
            onChange={(event) => narrow(() => setRepFilter(event.target.value))}
          >
            <option value="">{t("clients.repFilter.all")}</option>
            <option value={NO_REP}>{t("clients.repFilter.none")}</option>
            {activeReps.map((rep) => (
              <option key={rep.code} value={rep.code}>
                {rep.name} ({rep.code})
              </option>
            ))}
          </Select>
        ) : null}
      </Toolbar>

      {mayAssign && rows.length > 0 ? (
        <Toolbar
          actions={
            <>
              <Button
                size="sm"
                onClick={() =>
                  setSelected((now) => {
                    const next = new Set(now);
                    for (const row of rows) {
                      if (allOnPage) next.delete(row.client.id);
                      else next.add(row.client.id);
                    }
                    return next;
                  })
                }
              >
                {allOnPage ? t("clients.assign.unpickPage") : t("clients.assign.pickPage")}
              </Button>
              {selected.size > 0 ? (
                <>
                  <span className="hardauto-cell__muted">
                    {t("clients.assign.picked", { count: selected.size })}
                  </span>
                  <Button size="sm" onClick={() => setSelected(new Set())}>
                    {t("clients.assign.clear")}
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => setAssigning(true)}>
                    {t("clients.assign.open")}
                  </Button>
                </>
              ) : null}
            </>
          }
        />
      ) : null}

      {report ? (
        <Alert
          tone={report.rejected.length ? "warning" : "success"}
          title={t("clients.assign.report", {
            assigned: report.assigned,
            unchanged: report.unchanged,
          })}
          action={<Button onClick={() => setReport(null)}>{t("clients.assign.dismiss")}</Button>}
        >
          {report.rejected.length ? (
            <ul className="hardauto-rep-report" aria-label={t("clients.assign.rejectedLabel")}>
              {report.rejected.map((line) => (
                <li key={`${line.row}-${line.reason}`}>
                  {t("clients.assign.line", { row: line.row })}{" "}
                  {t(`clients.assign.reason.${line.reason}`, {
                    value: line.value,
                    defaultValue: line.reason,
                  })}
                </li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}

      {creating ? (
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!code.trim() || !company.trim()) return;
            void action.run("create", async () => {
              try {
                await api.createClient({ code: code.trim(), company_name: company.trim() });
                setCode("");
                setCompany("");
                setCreating(false);
                clients.reload();
              } catch {
                toast.show(t("error.save"), { tone: "danger" });
              }
            });
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("clients.newCode")} required>
              {(props) => (
                <Input {...props} value={code} onChange={(event) => setCode(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("clients.newCompany")} required>
              {(props) => (
                <Input
                  {...props}
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("create")}
              busyLabel={t("clients.save")}
            >
              {t("clients.save")}
            </Button>
            <Button onClick={() => setCreating(false)}>{t("clients.cancel")}</Button>
          </div>
        </form>
      ) : null}

      <DataTable<ClientRow>
        caption={t("clients.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.client.id}
        loading={clients.loading}
        loadingLabel={t("clients.loading")}
        error={
          clients.failed
            ? { title: t("error.title"), onRetry: clients.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={
          isNarrowed
            ? { title: t("clients.filtered.title"), description: t("clients.filtered.body") }
            : { title: t("clients.empty.title"), description: t("clients.empty.body") }
        }
      />

      <Dialog
        open={assigning}
        onClose={() => setAssigning(false)}
        title={t("clients.assign.title", { count: selected.size })}
        closeLabel={t("clients.cancel")}
        footer={
          <>
            <Button onClick={() => setAssigning(false)}>{t("clients.cancel")}</Button>
            <Button
              variant="primary"
              busy={action.isBusy("assign")}
              busyLabel={t("clients.assign.submit")}
              onClick={assign}
            >
              {t("clients.assign.submit")}
            </Button>
          </>
        }
      >
        <div className="hardauto-form">
          <Field label={t("clients.assign.to")}>
            {(props) => (
              <Select
                {...props}
                value={assignTo}
                onChange={(event) => setAssignTo(event.target.value)}
              >
                <option value="">{t("clients.assign.nobody")}</option>
                {activeReps.map((rep) => (
                  <option key={rep.code} value={rep.code}>
                    {rep.name} ({rep.code})
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Dialog>

      {clients.data?.next_cursor || cursor ? (
        <Toolbar
          actions={
            <>
              <Button onClick={() => setCursor(null)} disabled={cursor === null}>
                {t("client.terms.all")}
              </Button>
              <Button
                variant="primary"
                onClick={() => setCursor(clients.data?.next_cursor ?? null)}
                disabled={!clients.data?.next_cursor}
              >
                →
              </Button>
            </>
          }
        />
      ) : null}
    </Page>
  );
}
